import {
  Box,
  Grid,
  VStack,
  Text,
  HStack,
  useToast,
  Icon,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react'
import Sticky from 'react-stickynode'
import { useTranslation, Trans } from 'next-i18next'
import { useRouter } from 'next/router'
import { AiFillCheckCircle } from 'react-icons/ai'
import { saveAs } from 'file-saver'

import { GET_DATASHEET } from '@/routes/resources'

import { logEvent, useUTMContext } from '@/lib/gtm'
import env from '@/lib/env'
import requests from '@/lib/requests'

import Form from '@/features/resources/components/Form'
import { listStyle } from '@/features/landing/components/Carousel/Contents/style'

import { DEFAULT_TOAST_OPTIONS } from '@/lib/utils/format/toast'

export const RESOURCE_TITLE = 'implementation_datasheet'

function DatasheetContent(): React.ReactElement {
  const {
    t: tDatasheet,
    i18n: { language },
  } = useTranslation('datasheet')
  const { t: tResource } = useTranslation('resource')

  const toast = useToast()
  const router = useRouter()

  const { data: utm } = useUTMContext()

  return (
    <Box
      sx={{
        bg: 'secondary.800',
        w: 'full',
        '& > *': {
          px: 0,
        },
      }}
    >
      <Grid
        id="get-resource-container"
        layerStyle={{ md: 'container' }}
        sx={{
          gridTemplateColumns: {
            base: '1fr',
            md: '2fr 1fr',
          },
          gridGap: {
            base: 8,
            sm: 10,
          },
        }}
      >
        <VStack
          spacing={{ base: 8, sm: 10 }}
          layerStyle={{ base: 'container', md: '' }}
          sx={{
            alignItems: 'flex-start',
            color: 'white',
            mb: 8,
            pt: {
              base: 24,
              md: 10,
            },
            pb: 0,
          }}
        >
          <Box>
            <Text
              as="h2"
              sx={{
                fontSize: { base: 'lg', sm: '2xl' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tDatasheet('details.0.title')}
            </Text>
            <Text
              sx={{
                fontSize: { base: 'md', sm: 'lg' },
              }}
            >
              <Trans t={tDatasheet} i18nKey="details.0.paragraph">
                <Text
                  as="span"
                  sx={{
                    color: 'white',
                    lineHeight: 'inherit',
                    fontWeight: 'medium',
                  }}
                >
                  PDPA Core
                </Text>
              </Trans>
            </Text>
            <UnorderedList sx={listStyle}>
              {new Array(4).fill(0).map((_, index) => {
                return (
                  <ListItem key={index}>
                    {tDatasheet(`details.0.list.${index}`)}
                  </ListItem>
                )
              })}
            </UnorderedList>
          </Box>
          <VStack sx={{ color: 'white', alignItems: 'flex-start' }}>
            <Text
              as="h2"
              sx={{
                fontSize: { base: 'lg', sm: '2xl' },
                fontWeight: 'medium',
                marginBottom: 4,
              }}
            >
              {tDatasheet('contents.title')}
            </Text>
            {new Array(8).fill(0).map((_, index) => {
              return (
                <HStack key={`${index}`}>
                  <Icon
                    as={AiFillCheckCircle}
                    color="green.450"
                    fontSize="4xl"
                  />
                  <Text
                    sx={{
                      fontSize: { base: 'md', sm: 'lg' },
                      fontWeight: 'medium',
                    }}
                  >
                    {tDatasheet(`contents.list.${index}`)}
                  </Text>
                </HStack>
              )
            })}
          </VStack>
        </VStack>
        <Sticky
          bottomBoundary="#get-resource-container > *:first-of-type"
          top={120}
        >
          <Form
            id="get-resource"
            resourceTitle={RESOURCE_TITLE}
            onSubmit={async (data) => {
              const response = await requests.post(
                `${env.APP_URL}/api/get-resource/`,
                { ...data, utm, language },
                {},
              )

              const resBody = response.data.data
              const downloadUrl: string = resBody.downloadUrl

              saveAs(downloadUrl)

              toast({
                ...DEFAULT_TOAST_OPTIONS,
                description: tResource('form.successToast'),
              })

              logEvent({
                ga: {
                  category: 'Guideline',
                  action: 'Download success',
                },
                fb: {
                  event: 'Lead',
                },
              })

              router.push(
                {
                  pathname: GET_DATASHEET.pathname,
                },
                undefined,
                {
                  locale: language,
                },
              )
            }}
            formsubmitButton={tDatasheet('form.submitButton')}
          />
        </Sticky>
      </Grid>
    </Box>
  )
}

export default DatasheetContent
